import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const POWER_WASHING_PROPOSAL_TEMPLATES = [
  "Residential Exterior Soft Wash",
  "Driveway & Concrete Cleaning",
  "Commercial Storefront & Sidewalk",
  "Fleet & Heavy Equipment Wash",
  "Graffiti & Stain Remediation",
];

export const POWER_WASHING_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Residential Power Washing Agreement",
    description: "One-time exterior cleaning contract for siding, driveways, decks, fences, and patios with surface-specific PSI guidance.",
    content: `RESIDENTIAL POWER WASHING SERVICE AGREEMENT

This Power Washing Service Agreement ("Agreement") is entered into as of {{date}} by and between:

POWER WASHING PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________   Insurance Cert #: ________________
(hereinafter referred to as "Provider")

HOMEOWNER / CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SERVICE DESCRIPTION

Property Address: {{project_address}}
Service Type: {{project_description}}

Provider agrees to furnish all labor, equipment, water-reclamation containment (where required), detergents, and surfactants necessary to perform exterior cleaning at the above property.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Standard residential services may include:
• House siding soft-wash (vinyl, stucco, brick, fiber cement) — low-pressure SH-blend
• Driveway, sidewalk, and patio surface cleaning — surface cleaner with hot/cold water
• Deck and fence cleaning — wood-safe pressure and brightener
• Roof soft-wash — algae and lichen treatment (no high-pressure roof washing)
• Window pre-rinse and screen cleaning
• Gutter face cleaning (exterior only — interior cleanout quoted separately)

──────────────────────────────────────────────────

3. SURFACE-SPECIFIC METHODS

A. SOFT-WASH (siding, roofs, painted surfaces): 100–500 PSI with sodium hypochlorite blend and surfactant.
B. PRESSURE WASH (concrete flatwork): 2,000–4,000 PSI with surface cleaner attachment.
C. WOOD SURFACES (decks, fences): 800–1,500 PSI followed by neutralizing brightener.
D. Provider will adjust pressure, nozzle, and chemistry per manufacturer recommendations to avoid surface damage.

──────────────────────────────────────────────────

4. SERVICE TIMELINE

{{estimated_hours}}

Start Date: ________________
Estimated Completion: ________________
Weather Contingency: Provider may reschedule for rain, lightning, sustained winds >20 mph, or temperatures below 40°F.

──────────────────────────────────────────────────

5. CLIENT RESPONSIBILITIES

Client agrees to:
• Provide access to a working exterior water spigot rated at minimum 4 GPM
• Move vehicles, patio furniture, planters, and grills from work areas
• Close all windows, doors, and storm windows prior to service
• Notify Provider of any loose siding, damaged caulk, broken glass, or pre-existing surface conditions
• Cover or relocate sensitive plantings (Provider will pre-water surrounding vegetation)

──────────────────────────────────────────────────

6. COMPENSATION

Service Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Payment is due upon completion of services unless otherwise agreed.
• Late payments exceeding fifteen (15) days accrue interest at 1.5% per month.

──────────────────────────────────────────────────

7. INSURANCE & LIABILITY

Provider maintains:
• Commercial General Liability: $1,000,000 per occurrence / $2,000,000 aggregate
• Workers' Compensation: As required by state law
• Provider is NOT liable for damage to: rotted wood, failed caulk, loose paint, oxidized siding, broken/cracked window seals, deteriorated mortar, or pre-existing surface defects.
• Client agrees to disclose any known surface vulnerabilities prior to service.

──────────────────────────────────────────────────

8. SATISFACTION GUARANTEE

Provider guarantees professional results. If Client identifies missed areas within seven (7) days of service, Provider will return at no additional charge to address them, provided the issue is within the original agreed scope.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Commercial Flatwork & Storefront Contract",
    description: "Recurring or one-time concrete flatwork cleaning for retail, restaurant, and commercial properties — includes gum removal and after-hours scheduling.",
    content: `COMMERCIAL POWER WASHING SERVICE CONTRACT

This Commercial Power Washing Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

PROPERTY OWNER / FACILITY MANAGER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PROPERTY INFORMATION

Property Address: {{project_address}}
Property Type: {{project_description}}
Approximate Cleaned Area: ________________ sq ft

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Standard commercial services include:
A. CONCRETE FLATWORK
• Sidewalks, walkways, and entryway aprons — surface cleaner at 3,000–4,000 PSI hot water
• Drive-thru lanes, parking lot stalls, and trash enclosures (dumpster pads)
• Loading docks and rear-of-house service areas

B. STOREFRONT & ENTRY
• Façade soft-wash (low-pressure) for awnings, signage, and EIFS surfaces
• Entryway gum removal (steam or rotary attachment)
• Glass pre-rinse

C. SPECIALTY ADD-ONS (quoted separately)
• Graffiti removal with paint-specific solvents
• Oil-stain remediation with degreaser and surface cleaner
• Rust removal (oxalic-acid based treatment)

──────────────────────────────────────────────────

3. SCHEDULE & ACCESS

{{estimated_hours}}

Service Window: ________________ (typically 10 PM – 6 AM for storefronts)
Frequency: ________________

Provider shall coordinate with Client to perform services outside business hours where required and shall not impede customer or vehicular access during operating hours.

──────────────────────────────────────────────────

4. WATER, RECLAMATION & EPA COMPLIANCE

A. Provider shall comply with EPA Clean Water Act and local stormwater ordinances. Wash water shall NOT be permitted to enter storm drains where prohibited.
B. Where required, Provider will deploy berms, mats, vacuum-recovery units, or filter socks to capture and properly dispose of effluent.
C. Provider maintains documentation of waste-water disposal at approved facilities.
D. Client shall provide a working water source rated at minimum 5 GPM, or Provider shall supply water from a hauled tank (additional fee may apply).

──────────────────────────────────────────────────

5. COMPENSATION

Service Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Recurring service invoiced monthly per the agreed frequency.
• One-time projects invoiced upon completion.
• Net 30 standard unless otherwise specified.

──────────────────────────────────────────────────

6. INSURANCE

Provider maintains:
• Commercial General Liability: $2,000,000 per occurrence / $5,000,000 aggregate
• Workers' Compensation: Statutory limits
• Pollution Liability / Environmental Coverage: $1,000,000
• Auto Liability: $1,000,000 (combined single limit)

Certificates of insurance naming Client as additional insured provided upon request.

──────────────────────────────────────────────────

7. TERM & RENEWAL

Initial Term: {{contract_length}} {{contract_length_unit}}
Auto-Renewal: Successive terms of equal length unless either party provides thirty (30) days' written notice.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Fleet & Heavy Equipment Wash Agreement",
    description: "Recurring wash contract for trucks, trailers, construction equipment, and fleets — includes degreasing and DOT-compliance prep.",
    content: `FLEET & HEAVY EQUIPMENT WASH AGREEMENT

This Fleet Wash Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

FLEET OWNER / OPERATOR:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. ASSETS COVERED

Fleet Location: {{project_address}}
Asset Description: {{project_description}}
Approximate Vehicle / Unit Count: ________________

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Standard fleet services include:
• Two-step (acidic + alkaline) presoak with hot-water rinse
• Aluminum-safe brightener for trailers and tanks
• Wheel and tire cleaning with degreaser
• Undercarriage rinse for road salt and grime
• Engine bay degrease (on request — additional fee)
• DOT inspection prep wash with attention to lighting, reflectors, and DOT numbers

──────────────────────────────────────────────────

3. SCHEDULE

{{estimated_hours}}

Frequency: ________________
Service Window: ________________
Provider shall perform services on-site at Client's yard or terminal during agreed hours.

──────────────────────────────────────────────────

4. WATER RECLAMATION

Provider shall use water-recovery mats, berms, and vacuum systems to capture wash water in compliance with EPA and local regulations. Effluent shall be transported to an approved treatment facility.

──────────────────────────────────────────────────

5. COMPENSATION

Service Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Per-Unit Rate: \$________ per vehicle / unit
Minimum Visit Charge: \$________
Invoiced monthly based on actual units serviced.

──────────────────────────────────────────────────

6. INSURANCE

Same coverage requirements as Commercial Power Washing Contract apply, including Pollution Liability of $1,000,000 minimum.

──────────────────────────────────────────────────

7. TERM

Initial Term: {{contract_length}} {{contract_length_unit}}
Auto-Renewal applies unless thirty (30) days' written notice is given.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Specialty Remediation Contract",
    description: "Single-service contract for graffiti removal, oil-stain remediation, rust removal, and surface restoration with no-mark guarantee.",
    content: `SPECIALTY SURFACE REMEDIATION CONTRACT

This Specialty Remediation Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PROJECT DETAILS

Property Address: {{project_address}}
Remediation Type: {{project_description}}
Affected Area: ________________ sq ft

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Specialty services include (as applicable):
• GRAFFITI REMOVAL — solvent identification by paint type, dwell-time application, low-pressure rinse, and shadow-stain neutralization
• OIL & GREASE REMEDIATION — degreaser application with agitation, hot-water surface clean, secondary chemistry pass for deep stains
• RUST REMOVAL — oxalic acid or commercial rust dissolver, dwell, neutralizing rinse
• EFFLORESCENCE REMOVAL — masonry-safe acidic wash and full neutralization
• ORGANIC STAIN REMOVAL — algae, lichen, mold, mildew with sodium hypochlorite blend

──────────────────────────────────────────────────

3. SUBSTRATE TESTING

Provider shall perform a small test patch in an inconspicuous area before full application to verify chemistry compatibility and confirm results meet Client expectations.

──────────────────────────────────────────────────

4. EXPECTED OUTCOMES

A. Provider commits to best industry-standard results but cannot guarantee 100% removal of stains aged longer than 90 days, ghosted graffiti shadows, or stains that have permanently etched the substrate.
B. Multiple treatment passes may be required and quoted separately.

──────────────────────────────────────────────────

5. TIMELINE

{{estimated_hours}}

──────────────────────────────────────────────────

6. COMPENSATION

Project Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment due upon completion unless otherwise agreed.

──────────────────────────────────────────────────

7. INSURANCE & SAFETY

Provider maintains all insurance per Commercial Contract, plus chemical-handling certifications and SDS documentation available on request.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];
